/**
 * Staff feature requests and bug reports (WP4-FEEDBACK), modelled on the
 * Simplex "Feature Requests" feature: any signed-in staff member can file a
 * request from the top bar, see the practice's requests and add notes; a
 * manager (`feedback.manage`) sets the status and priority.
 *
 * Requests are stored in Velora's own database only. Nothing is sent to an
 * outside service. The automatic context is the route pattern (for example
 * `/patients/$patientId`, never the filled-in URL), the module, the office,
 * the user and the build; it never holds patient data.
 */
import { z } from 'zod';

/* ------------------------------------------------------------------------ */
/* Vocabularies (same values as Simplex)                                     */
/* ------------------------------------------------------------------------ */

export const FEEDBACK_TYPES = ['bug', 'improvement', 'feature', 'other'] as const;
export const FeedbackTypeSchema = z.enum(FEEDBACK_TYPES);
export type FeedbackType = z.infer<typeof FeedbackTypeSchema>;

export const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
  bug: 'Bug report',
  improvement: 'Improvement',
  feature: 'Feature request',
  other: 'Other',
};

export const FEEDBACK_TYPE_HELP: Record<FeedbackType, string> = {
  bug: 'Something is broken or not working as expected.',
  improvement: 'An existing feature could work better.',
  feature: 'A new capability you would like added.',
  other: 'Question, feedback, or anything else.',
};

export const FEEDBACK_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export const FeedbackPrioritySchema = z.enum(FEEDBACK_PRIORITIES);
export type FeedbackPriority = z.infer<typeof FeedbackPrioritySchema>;

export const FEEDBACK_PRIORITY_LABELS: Record<FeedbackPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

export const FEEDBACK_STATUSES = [
  'new',
  'reviewing',
  'planned',
  'in_progress',
  'resolved',
  'declined',
] as const;
export const FeedbackStatusSchema = z.enum(FEEDBACK_STATUSES);
export type FeedbackStatus = z.infer<typeof FeedbackStatusSchema>;

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  new: 'New',
  reviewing: 'Reviewing',
  planned: 'Planned',
  in_progress: 'In progress',
  resolved: 'Resolved',
  declined: 'Declined',
};

/** Still being worked on: the list's default filter ("Active"). */
export const FEEDBACK_ACTIVE_STATUSES: readonly FeedbackStatus[] = [
  'new',
  'reviewing',
  'planned',
  'in_progress',
];

/** Statuses that close a request (`resolvedAt` is set). */
export const FEEDBACK_CLOSED_STATUSES: readonly FeedbackStatus[] = ['resolved', 'declined'];

export const FEEDBACK_TITLE_MAX = 200;
export const FEEDBACK_DESCRIPTION_MAX = 10_000;
export const FEEDBACK_NOTE_MAX = 5_000;
/** Per attachment; Simplex allows 20 MB, Velora keeps screenshots small. */
export const FEEDBACK_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const FEEDBACK_ATTACHMENTS_PER_REQUEST = 10;
export const FEEDBACK_ATTACHMENT_MIME = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain',
  'text/csv',
] as const;

/** Module id slug (`schedule`, `chart`, ...) or `other`; mirrors manifest ids. */
export const FeedbackAreaSchema = z
  .string()
  .regex(/^[a-z][a-z0-9-]{0,63}$/)
  .nullable();

/* ------------------------------------------------------------------------ */
/* Context captured with a request                                           */
/* ------------------------------------------------------------------------ */

/**
 * Route pattern as declared in a manifest, or a pathname with every id-like
 * segment replaced by `:id`. Query strings and hashes are never kept.
 */
export const FeedbackRoutePathSchema = z
  .string()
  .max(300)
  .regex(/^\/[A-Za-z0-9/$:_.-]*$/, 'route must be a path pattern without a query');

export const FeedbackClientContextSchema = z.object({
  routePath: FeedbackRoutePathSchema.nullable().default(null),
  routeTitle: z.string().max(120).nullable().default(null),
  moduleId: FeedbackAreaSchema.default(null),
  viewport: z
    .string()
    .regex(/^\d{2,5}x\d{2,5}$/)
    .nullable()
    .default(null),
});
export type FeedbackClientContext = z.infer<typeof FeedbackClientContextSchema>;

export const FeedbackContextSchema = FeedbackClientContextSchema.extend({
  appVersion: z.string(),
  officeName: z.string().nullable(),
  userAgent: z.string().nullable(),
});
export type FeedbackContext = z.infer<typeof FeedbackContextSchema>;

/**
 * Replaces id-like path segments (uuids, numbers, long tokens) with `:id` and
 * drops the query and hash, so a pathname such as `/patients/1f0c...` never
 * leaves the client with a record id in it.
 */
export function sanitizeRoutePath(pathname: string): string {
  const path = pathname.split(/[?#]/)[0] ?? '/';
  const segments = path
    .split('/')
    .filter((s) => s.length > 0)
    .map((s) => {
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) return ':id';
      if (/\d/.test(s) && s.length >= 4) return ':id';
      if (/^\d+$/.test(s)) return ':id';
      if (!/^[A-Za-z0-9$:_.-]+$/.test(s)) return ':id';
      if (s.length > 40) return ':id';
      return s;
    });
  return `/${segments.join('/')}`.slice(0, 300);
}

/* ------------------------------------------------------------------------ */
/* Requests                                                                  */
/* ------------------------------------------------------------------------ */

export const FeedbackCreateBody = z.object({
  type: FeedbackTypeSchema.default('feature'),
  title: z.string().trim().min(1).max(FEEDBACK_TITLE_MAX),
  description: z.string().trim().max(FEEDBACK_DESCRIPTION_MAX).nullable().default(null),
  area: FeedbackAreaSchema.default(null),
  priority: FeedbackPrioritySchema.default('normal'),
  context: FeedbackClientContextSchema.default({
    routePath: null,
    routeTitle: null,
    moduleId: null,
    viewport: null,
  }),
});
export type FeedbackCreateBody = z.infer<typeof FeedbackCreateBody>;

export const FeedbackStatusBody = z.object({
  status: FeedbackStatusSchema,
  priority: FeedbackPrioritySchema,
});
export type FeedbackStatusBody = z.infer<typeof FeedbackStatusBody>;

export const FeedbackNoteBody = z.object({
  note: z.string().trim().min(1).max(FEEDBACK_NOTE_MAX),
});
export type FeedbackNoteBody = z.infer<typeof FeedbackNoteBody>;

export const FeedbackListQuery = z.object({
  status: z.union([FeedbackStatusSchema, z.enum(['active', 'all'])]).default('active'),
  type: FeedbackTypeSchema.optional(),
  area: z
    .string()
    .regex(/^[a-z][a-z0-9-]{0,63}$/)
    .optional(),
});
export type FeedbackListQuery = z.infer<typeof FeedbackListQuery>;

export const FeedbackAttachmentSchema = z.object({
  id: z.uuid(),
  filename: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  uploadedByName: z.string(),
  createdAt: z.string(),
  noteId: z.uuid().nullable(),
});
export type FeedbackAttachment = z.infer<typeof FeedbackAttachmentSchema>;

export const FeedbackNoteSchema = z.object({
  id: z.uuid(),
  kind: z.enum(['note', 'status']),
  note: z.string(),
  authorName: z.string(),
  createdAt: z.string(),
});
export type FeedbackNote = z.infer<typeof FeedbackNoteSchema>;

export const FeedbackSummarySchema = z.object({
  id: z.uuid(),
  number: z.number().int(),
  locationId: z.uuid(),
  type: FeedbackTypeSchema,
  title: z.string(),
  area: z.string().nullable(),
  priority: FeedbackPrioritySchema,
  status: FeedbackStatusSchema,
  submittedByName: z.string(),
  mine: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  resolvedAt: z.string().nullable(),
  noteCount: z.number().int(),
  attachmentCount: z.number().int(),
});
export type FeedbackSummary = z.infer<typeof FeedbackSummarySchema>;

export const FeedbackCountsSchema = z.object({
  total: z.number().int(),
  new: z.number().int(),
  working: z.number().int(),
  resolved: z.number().int(),
});
export type FeedbackCounts = z.infer<typeof FeedbackCountsSchema>;

export const FeedbackListResponse = z.object({
  items: z.array(FeedbackSummarySchema),
  counts: FeedbackCountsSchema,
});
export type FeedbackListResponse = z.infer<typeof FeedbackListResponse>;

export const FeedbackDetailSchema = FeedbackSummarySchema.extend({
  description: z.string().nullable(),
  context: FeedbackContextSchema,
  notes: z.array(FeedbackNoteSchema),
  attachments: z.array(FeedbackAttachmentSchema),
});
export type FeedbackDetail = z.infer<typeof FeedbackDetailSchema>;

export const FeedbackAttachmentUploadQuery = z.object({
  filename: z.string().trim().min(1).max(200),
  noteId: z.uuid().optional(),
});

/* ------------------------------------------------------------------------ */
/* C2G Admin: feedback from every practice                                   */
/* ------------------------------------------------------------------------ */

/**
 * The cross-practice list Computers2Go support sees in C2G Admin. Titles,
 * descriptions and notes are staff-written; attachment bytes never leave the
 * practice (support sees names, types and sizes only).
 */
export const SupportFeedbackQuery = z.object({
  status: z.union([FeedbackStatusSchema, z.enum(['active', 'all'])]).default('active'),
  practiceId: z.uuid().optional(),
});
export type SupportFeedbackQuery = z.infer<typeof SupportFeedbackQuery>;

export const SupportFeedbackItemSchema = z.object({
  id: z.uuid(),
  number: z.number().int(),
  practiceId: z.uuid(),
  practiceName: z.string(),
  locationId: z.uuid(),
  officeName: z.string().nullable(),
  type: FeedbackTypeSchema,
  title: z.string(),
  area: z.string().nullable(),
  routePath: z.string().nullable(),
  priority: FeedbackPrioritySchema,
  status: FeedbackStatusSchema,
  submittedByName: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  noteCount: z.number().int(),
  attachmentCount: z.number().int(),
  hasScreenshot: z.boolean(),
});
export type SupportFeedbackItem = z.infer<typeof SupportFeedbackItemSchema>;

export const SupportFeedbackListResponse = z.object({
  items: z.array(SupportFeedbackItemSchema),
  counts: FeedbackCountsSchema,
});
export type SupportFeedbackListResponse = z.infer<typeof SupportFeedbackListResponse>;

export const SupportFeedbackDetailSchema = SupportFeedbackItemSchema.omit({
  noteCount: true,
  attachmentCount: true,
  hasScreenshot: true,
}).extend({
  resolvedAt: z.string().nullable(),
  description: z.string().nullable(),
  context: FeedbackContextSchema,
  notes: z.array(FeedbackNoteSchema),
  /** Names, types and sizes only; the files stay in the practice. */
  attachments: z.array(FeedbackAttachmentSchema.omit({ noteId: true })),
});
export type SupportFeedbackDetail = z.infer<typeof SupportFeedbackDetailSchema>;

/* ------------------------------------------------------------------------ */
/* What's new                                                                */
/* ------------------------------------------------------------------------ */

/**
 * A short announcement Computers2Go writes when a module ships: a summary
 * and a few walkthrough steps, shown once after sign-in until the user
 * dismisses it ("Don't show again"), and reopened from the user menu.
 */
export const WHATS_NEW_STEPS_MAX = 6;

/** In-app path, optionally with a query (`/phones?tab=missed`); never a full URL. */
export const WhatsNewRouteSchema = z
  .string()
  .max(200)
  .regex(/^\/[A-Za-z0-9/$:_.-]*(\?[A-Za-z0-9=&_.-]*)?$/, 'route must be an in-app path');

export const WhatsNewStepSchema = z.object({
  title: z.string().trim().min(1).max(120),
  body: z.string().trim().min(1).max(600),
  route: WhatsNewRouteSchema.nullable().default(null),
});
export type WhatsNewStep = z.infer<typeof WhatsNewStepSchema>;

export const WhatsNewItemSchema = z.object({
  id: z.uuid(),
  key: z.string(),
  moduleId: z.string().nullable(),
  title: z.string(),
  summary: z.string(),
  steps: z.array(WhatsNewStepSchema),
  releasedAt: z.string(),
  dismissed: z.boolean(),
});
export type WhatsNewItem = z.infer<typeof WhatsNewItemSchema>;

export const WhatsNewQuery = z.object({
  /** `all`: also the ones already dismissed (the user menu reopens them). */
  include: z.enum(['new', 'all']).default('new'),
});
export type WhatsNewQuery = z.infer<typeof WhatsNewQuery>;

export const WhatsNewResponse = z.object({ items: z.array(WhatsNewItemSchema) });
export type WhatsNewResponse = z.infer<typeof WhatsNewResponse>;

export const AnnouncementKeySchema = z
  .string()
  .trim()
  .regex(/^[a-z0-9][a-z0-9.-]{2,79}$/, 'lowercase letters, digits, dots and dashes');

/** C2G Admin create / edit. */
export const AnnouncementSaveBody = z.object({
  key: AnnouncementKeySchema,
  moduleId: FeedbackAreaSchema.default(null),
  title: z.string().trim().min(1).max(120),
  summary: z.string().trim().min(1).max(600),
  steps: z.array(WhatsNewStepSchema).min(1).max(WHATS_NEW_STEPS_MAX),
  releasedAt: z.iso.datetime({ offset: true }).nullable().default(null),
  active: z.boolean().default(true),
  /** Only users holding this permission (in any office) see it. */
  requiredPermission: z
    .string()
    .regex(/^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/)
    .nullable()
    .default(null),
  /** Only practices with this module turned on (in any office the user can open) see it. */
  entitlementKey: z
    .string()
    .regex(/^[a-z][a-z0-9_.-]{0,63}$/)
    .nullable()
    .default(null),
});
export type AnnouncementSaveBody = z.infer<typeof AnnouncementSaveBody>;

export const AnnouncementSchema = z.object({
  id: z.uuid(),
  key: z.string(),
  moduleId: z.string().nullable(),
  title: z.string(),
  summary: z.string(),
  steps: z.array(WhatsNewStepSchema),
  releasedAt: z.string(),
  active: z.boolean(),
  requiredPermission: z.string().nullable(),
  entitlementKey: z.string().nullable(),
  updatedAt: z.string(),
  dismissedCount: z.number().int(),
});
export type Announcement = z.infer<typeof AnnouncementSchema>;

export const AnnouncementListResponse = z.object({ items: z.array(AnnouncementSchema) });
export type AnnouncementListResponse = z.infer<typeof AnnouncementListResponse>;
